
import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// Language code mapping
export const LANGUAGE_CODES = {
  "English": "EN",
  "Spanish": "ES",
  "French": "FR",
  "German": "DE",
  "Japanese": "JA",
  "Chinese": "ZH"
};

export type SupportedLanguage = keyof typeof LANGUAGE_CODES;

interface TranslationResult {
  translations: Array<{
    detected_source_language: string;
    text: string;
  }>;
}

interface CachedTranslation {
  text: string;
  targetLang: string;
  result: string;
  timestamp: number;
}

export const useTranslation = () => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Create a local cache for translations
  const translationCache = useRef<CachedTranslation[]>([]);
  const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutes in milliseconds
  
  // Function to check if we have a cached translation
  const getCachedTranslation = (text: string, targetLang: string): string | null => {
    const now = Date.now();
    const cached = translationCache.current.find(
      item => item.text === text && 
      item.targetLang === targetLang && 
      (now - item.timestamp) < CACHE_EXPIRY
    );
    
    return cached ? cached.result : null;
  };
  
  // Function to add a translation to the cache
  const cacheTranslation = (text: string, targetLang: string, result: string): void => {
    // Remove old entries for the same text/language pair
    translationCache.current = translationCache.current.filter(
      item => !(item.text === text && item.targetLang === targetLang)
    );
    
    // Add the new translation to the cache
    translationCache.current.push({
      text,
      targetLang,
      result,
      timestamp: Date.now()
    });
    
    // Limit cache size to 100 entries
    if (translationCache.current.length > 100) {
      translationCache.current.shift(); // Remove oldest entry
    }
  };

  const translateText = useCallback(async (
    text: string,
    targetLang: string,
    sourceLang?: string,
    forceRefresh: boolean = false
  ): Promise<string> => {
    if (!text || text.trim() === '') {
      return text;
    }
    
    // Check local cache first (if not forcing a refresh)
    if (!forceRefresh) {
      const cachedResult = getCachedTranslation(text, targetLang);
      if (cachedResult) {
        return cachedResult;
      }
    }

    setIsTranslating(true);
    setError(null);

    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text,
          targetLang,
          sourceLang,
          forceRefresh
        },
      });

      if (error) {
        console.error('Translation error:', error);
        setError(error.message);
        toast({
          title: 'Translation failed',
          description: 'There was an error translating the content',
          variant: 'destructive',
        });
        return text;
      }

      const result = data as TranslationResult;
      const translatedText = result.translations[0]?.text || text;
      
      // Cache the successful translation
      cacheTranslation(text, targetLang, translatedText);
      
      return translatedText;
    } catch (err) {
      console.error('Translation error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      toast({
        title: 'Translation failed',
        description: 'There was an error translating the content',
        variant: 'destructive',
      });
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, []);

  const translateBlockContent = useCallback(async (
    blocks: any[],
    targetLang: string,
    forceRefresh: boolean = false
  ): Promise<any[]> => {
    if (!blocks || blocks.length === 0) {
      return blocks;
    }

    setIsTranslating(true);
    setError(null);

    try {
      // Create a deep copy of blocks to avoid mutating the original
      const translatedBlocks = JSON.parse(JSON.stringify(blocks));
      
      // Process each block for translation
      for (let i = 0; i < translatedBlocks.length; i++) {
        const block = translatedBlocks[i];
        
        if (!block) continue;
        
        // Extract the text content based on block type
        let textToTranslate = '';
        
        if (block.type === 'paragraph' && block.content?.text) {
          textToTranslate = block.content.text;
        } else if (block.type === 'header' && block.content?.text) {
          textToTranslate = block.content.text;
        } else if (block.type === 'list' && block.content?.items) {
          // For list items, translate each item separately
          const translatedItems = [];
          for (const item of block.content.items) {
            const translatedItem = await translateText(item, targetLang, undefined, forceRefresh);
            translatedItems.push(translatedItem);
          }
          block.content.items = translatedItems;
          continue;
        } else if (block.type === 'quote' && block.content?.text) {
          textToTranslate = block.content.text;
          if (block.content.caption) {
            block.content.caption = await translateText(
              block.content.caption, 
              targetLang, 
              undefined,
              forceRefresh
            );
          }
        }
        
        // Translate the text if available
        if (textToTranslate) {
          const translatedText = await translateText(
            textToTranslate, 
            targetLang, 
            undefined,
            forceRefresh
          );
          if (block.type === 'paragraph' || block.type === 'header' || block.type === 'quote') {
            block.content.text = translatedText;
          }
        }
      }
      
      return translatedBlocks;
    } catch (err) {
      console.error('Block translation error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
      toast({
        title: 'Translation failed',
        description: 'There was an error translating the content',
        variant: 'destructive',
      });
      return blocks;
    } finally {
      setIsTranslating(false);
    }
  }, [translateText]);

  // Function to invalidate the cache for a specific language or all languages
  const invalidateCache = useCallback((targetLang?: string) => {
    if (targetLang) {
      // Remove entries only for the specified language
      translationCache.current = translationCache.current.filter(
        item => item.targetLang !== targetLang
      );
    } else {
      // Clear the entire cache
      translationCache.current = [];
    }
  }, []);

  return {
    translateText,
    translateBlockContent,
    invalidateCache,
    isTranslating,
    error
  };
};
